export type LocalSpeechLang = 'zh-CN' | 'en-US';

type SpeakOptions = {
  lang?: LocalSpeechLang;
  rate?: number;
  pitch?: number;
  volume?: number;
  interrupt?: boolean;
};

let initialized = false;
let voicesCache: SpeechSynthesisVoice[] = [];

const getSynth = (): SpeechSynthesis | null => {
  if (typeof window === 'undefined') return null;
  if (!('speechSynthesis' in window)) return null;
  return window.speechSynthesis;
};

export const isLocalSpeechSupported = (): boolean => {
  if (typeof window === 'undefined') return false;
  return ('speechSynthesis' in window) && ('SpeechSynthesisUtterance' in window);
};

const refreshVoices = () => {
  const synth = getSynth();
  if (!synth) return;
  try {
    voicesCache = synth.getVoices() || [];
  } catch {
    voicesCache = [];
  }
};

const ensureInitialized = () => {
  if (initialized) return;
  initialized = true;
  refreshVoices();
  const synth = getSynth();
  if (!synth) return;
  try {
    synth.addEventListener('voiceschanged', refreshVoices);
  } catch {
    // ignore
  }
};

const pickVoice = (lang: LocalSpeechLang): SpeechSynthesisVoice | null => {
  const voices = Array.isArray(voicesCache) ? voicesCache : [];
  if (!voices.length) return null;
  const preferredPrefixes = lang === 'zh-CN'
    ? ['zh-cn', 'zh']
    : ['en-us', 'en'];
  for (const prefix of preferredPrefixes) {
    const hit = voices.find((v) => String(v?.lang || '').toLowerCase().startsWith(prefix));
    if (hit) return hit;
  }
  return voices[0] || null;
};

export const speakTextLocal = (text: string, options?: SpeakOptions): boolean => {
  const raw = String(text || '').trim();
  if (!raw) return false;
  if (!isLocalSpeechSupported()) return false;

  ensureInitialized();
  const synth = getSynth();
  if (!synth) return false;

  const lang = (options?.lang || 'zh-CN') as LocalSpeechLang;
  const rate = Math.min(2, Math.max(0.5, Number(options?.rate || 1)));
  const pitch = Math.min(2, Math.max(0, Number(options?.pitch || 1)));
  const volume = Math.min(1, Math.max(0, Number(options?.volume || 1)));

  try {
    if (options?.interrupt !== false) synth.cancel();
    const utter = new SpeechSynthesisUtterance(raw);
    utter.lang = lang;
    utter.rate = rate;
    utter.pitch = pitch;
    utter.volume = volume;
    const voice = pickVoice(lang);
    if (voice) utter.voice = voice;
    synth.speak(utter);
    return true;
  } catch {
    return false;
  }
};

